import type sv from '../sv/chat'

const chat: typeof sv = {
  composer: {
    placeholder: 'Write a message…',
    placeholderNew: 'Work with HUB',
    connectingVoice: 'Connecting microphone…',
    listening: 'Listening…',
    dictationLimit: 'Dictation stopped — maximum length reached.',
    dictationUnavailable: 'Dictation is not available.',
    dictationFailed: 'Dictation stopped. Please try again.',
    micDenied: 'The microphone is blocked — allow it in your browser.',
    placeholderBusy: 'Type – sent when the reply is done…',
    attachFile: 'Attach file',
    attach: 'Attach',
    addAttachment: 'Add attachment',
    removeAttachment: 'Remove {{name}}',
    addToChat: 'Add to chat',
    selections_one: '{{count}} selection',
    selections_other: '{{count}} selections',
    selectionsTitle: 'Selected text sent with the message',
    removeSelection: 'Remove selection',
    selectionTruncated: 'Cut to {{max}} characters',
    sendMessage: 'Send message',
    sendWithShortcut: 'Send ({{shortcut}})',
    stopGenerating: 'Stop generating',
    stopFailed: 'The reply could not be stopped. Try again.',
    stopConfirmTitle: 'The agent is still working',
    stopConfirmBody:
      'The agent is still working — do you really want to stop? The reply being written will be lost.',
    stopConfirmKeepWaiting: 'Keep waiting',
    stopConfirmStop: 'Stop anyway',
    startVoice: 'Start voice input',
    stopRecording: 'Stop recording',
    context: 'Context',
    contextUsage: 'Context usage',
    contextSystem: 'System',
    contextTools: 'Tools',
    contextMessages: 'Messages',
    contextTotal: 'Total',
    invalidResponse: 'Invalid response',
    uploadFailed: 'Upload failed ({{status}})',
    networkError: 'Network error',
    error: 'Error',
    projectDocument: 'Project document',
    file: 'File',
  },
  uiBlocks: {
    filter: 'Filter',
    rows_one: '{{count}} row',
    rows_other: '{{count}} rows',
    rowsFiltered: '{{shown}} of {{count}} rows',
    noRows: 'No matching rows',
    exportCsv: 'Export CSV',
    csvFilename: 'table',
    showAsTable: 'Show as table',
    showAsChart: 'Show as chart',
    other: 'Other',
    chartSummary: '{{kind}}: {{series}}. {{count}} categories, from {{min}} to {{max}}.',
    kindBar: 'Bar chart',
    kindLine: 'Line chart',
    kindPie: 'Pie chart',
    couldNotRender: 'This block could not be shown.',
    loading: 'Loading…',
  },
  attachmentPicker: {
    uploadFromComputer: 'Upload from computer',
    chooseDocuments: 'Choose documents',
    back: 'Back',
  },
  documentPicker: {
    searchPlaceholder: 'Search documents...',
    all: 'All',
    empty: 'No documents found',
    noneSelected: 'None selected',
    selected_one: '{{count}} selected',
    selected_other: '{{count}} selected',
    add: 'Add',
  },
  prompts: {
    suggested: 'Suggested',
    preparation: 'Which steps should the work preparation for lightweight walls include?',
    risk: 'What are the health and safety risks when installing lightweight walls with steel studs?',
    experience: 'What problems came up when installing the lightweight walls in stairwell B?',
  },
  thread: {
    syncing: 'Syncing chat...',
    loadOlder: 'Load older messages',
    loading: 'Loading...',
    typing: '{{names}} typing…',
    openWorkspace: 'Open workspace',
    disclaimer: 'HUB is AI and can make mistakes — double-check important details.',
    jumpToLatest: 'Jump to latest',
    jumpToLatestNew: 'New content below — jump to latest',
  },
  queue: {
    running: 'running',
    queued_one: '{{count}} queued',
    queued_other: '{{count}} queued',
    sentWhenDone: 'sent when the reply is done',
    remove: 'Remove "{{text}}" from the queue',
  },
  message: {
    deleteAria: 'Delete message',
    deletedByUser: 'Deleted by user',
    deleteTitle: 'Delete message?',
    deleteDescription:
      'The message will still show as "Deleted by user" for everyone in the chat. This cannot be undone.',
    cancel: 'Cancel',
    delete: 'Delete',
    attachmentDocument: 'document',
    quotedSelection: 'Selected text',
    attachmentFile: 'file',
    userFallback: 'User',
  },
  email: {
    open: 'Open the e-mail in the inbox',
    noSubject: '(no subject)',
    attachments_one: '{{count}} attachment',
    attachments_other: '{{count}} attachments',
    deleted: 'This e-mail was deleted',
  },
  actions: {
    play: 'Read aloud',
    stopPlayback: 'Stop playback',
    pausePlayback: 'Pause playback',
    resumePlayback: 'Resume playback',
    copy: 'Copy response',
    copied: 'Response copied',
    helpful: 'Mark response helpful',
    unhelpful: 'Mark response unhelpful',
    more: 'More response actions',
  },
  usage: {
    more: 'More about this reply',
    title: 'Usage',
    mode: 'Mode',
    tokensIn: 'Tokens in',
    tokensOut: 'Tokens out',
    reasoning: 'Reasoning',
    cache: 'Of which cached',
    total: 'Total',
    modelCalls: 'Model calls',
    credits: 'Credits',
    creditsBreakdown: 'LLM {{llm}} · tools {{tools}}',
    none: 'No usage data for this reply',
    words: 'Words',
    toolCalls: 'Tool calls',
    duration: 'Response time',
    durationSeconds: '{{seconds}} s',
    durationMinutes: '{{minutes}} min {{seconds}} s',
    showRaw: 'Show raw data (dev)',
  },
  status: {
    queued: 'Waiting for the agent',
    reasoning: 'Reasoning',
    toolCalling: 'Using tools',
    responding: 'Writing reply',
    thinking: 'Thinking',
    takingLonger: 'Taking longer than usual',
  },
  errors: {
    ttsFailed: 'Read-aloud failed.',
    provider_capability: 'The model provider rejected the request',
    provider_transient: 'The model service had a temporary problem',
    hard_error: 'This reply could not be generated',
    empty_reply: 'The agent finished without replying',
    unknown: 'The reply did not go through',
    interrupted: 'The reply was cut off before it finished',
    stopped: 'Stopped – the reply is incomplete',
    stoppedEmpty: 'Stopped before the reply began',
    interruptedHint:
      'The agent stopped responding mid-turn — steps that came after (such as updating a document) were probably not carried out. Try again if you like.',
    retryableHint: 'Try again — it usually works on the second attempt.',
    nonRetryableHint: 'A retry is unlikely to succeed — try rephrasing or shortening the question.',
    retrying: 'Retrying',
    retry: 'Try again',
    skip: 'Skip',
    toolFailed: 'The tool failed',
  },
  reasoning: {
    thinking: 'Thinking',
    done: 'Reasoned ({{seconds}}s)',
    label: 'Reasoning',
  },
  steps: {
    usingTools: 'Using tools ({{done}}/{{total}})',
    tools: 'Tools ({{total}})',
    noParams: 'No parameters',
    done: 'Done',
    request: 'Request',
    result: 'Result',
    copied: 'Copied',
    copyJson: 'Copy {{section}} as JSON',
  },
  tools: {
    /** Board wwfu: a project plugin (remote MCP) tool. */
    mcp: { label: '{{server}} · {{tool}}', activeLabel: '{{server}} · {{tool}}…' },
    browserNavigate: {
      label: 'Opened page in the browser',
      activeLabel: 'Opening page in the browser',
    },
    browserRead: { label: 'Read the web page', activeLabel: 'Reading the web page' },
    browserClick: { label: 'Clicked in the browser', activeLabel: 'Clicking in the browser' },
    browserType: { label: 'Typed in the browser', activeLabel: 'Typing in the browser' },
    browserPress: {
      label: 'Pressed a key in the browser',
      activeLabel: 'Pressing a key in the browser',
    },
    browserScroll: { label: 'Scrolled the web page', activeLabel: 'Scrolling the web page' },
    browserHandToUser: {
      label: 'Handed the browser to you',
      activeLabel: 'Waiting for you in the browser',
    },
    browserClose: { label: 'Closed the browser', activeLabel: 'Closing the browser' },
    vectorQueryTool: { label: 'Searched documents', activeLabel: 'Searching documents' },
    saveProjectProfile: {
      label: 'Saved the project settings',
      activeLabel: 'Saving the project settings',
    },
    archiveOnboardingChat: { label: 'Archived the chat', activeLabel: 'Archiving the chat' },
    rememberFact: { label: 'Saved to project memory', activeLabel: 'Saving to project memory' },
    recallProjectMemory: {
      label: 'Searched project memory',
      activeLabel: 'Searching project memory',
    },
    forgetMemory: {
      label: 'Removed from project memory',
      activeLabel: 'Removing from project memory',
    },
    listChats: { label: 'Listed chats', activeLabel: 'Listing chats' },
    readChat: { label: 'Read chat', activeLabel: 'Reading chat' },
    listTasks: { label: 'Listed tasks', activeLabel: 'Listing tasks' },
    listMembers: { label: 'Checked project members', activeLabel: 'Checking project members' },
    listDocuments: { label: 'Looked for documents', activeLabel: 'Looking for documents' },
    createTask: { label: 'Created task', activeLabel: 'Creating task' },
    updateTask: { label: 'Updated task', activeLabel: 'Updating task' },
    readDocument: { label: 'Read document', activeLabel: 'Reading document' },
    createDocument: { label: 'Created document', activeLabel: 'Creating document' },
    updateDocument: { label: 'Updated document', activeLabel: 'Updating document' },
    updateBlocks: { label: 'Edited document', activeLabel: 'Editing document' },
    replaceDocument: { label: 'Rewrote document', activeLabel: 'Rewriting document' },
    web_search: { label: 'Searched the web', activeLabel: 'Searching the web' },
    readSpreadsheetCsv: { label: 'Checked data', activeLabel: 'Checking data' },
    viewImage: { label: 'Looked at a picture again', activeLabel: 'Looking at a picture again' },
    listInboxEmails: { label: 'Listed inbox email', activeLabel: 'Listing inbox email' },
    readInboxEmail: { label: 'Read email', activeLabel: 'Reading email' },
    readEmailAttachment: { label: 'Read attachment', activeLabel: 'Reading attachment' },
    saveEmailAttachment: {
      label: 'Saved attachment to Documents',
      activeLabel: 'Saving attachment to Documents',
    },
    replyToEmail: { label: 'Replied to email', activeLabel: 'Replying to email' },
    archiveInboxEmail: { label: 'Archived email', activeLabel: 'Archiving email' },
    args: {
      query: 'Query',
      title: 'Title',
      documentId: 'Document',
      taskId: 'Task',
      content: 'Content',
      description: 'Description',
      status: 'Status',
      columnName: 'Column',
      priority: 'Priority',
      assigneeId: 'Assignee',
      dueDate: 'Due',
      tags: 'Tags',
      limit: 'Max count',
    },
    result: {
      waiting: 'Waiting for response…',
      done: 'Done',
      words_one: '{{count}} word',
      words_other: '{{count}} words',
      chars: '{{count}} characters',
      updated: 'updated {{date}}',
      created: 'created {{date}}',
      createdDocument: 'Created {{title}}',
      updatedDocument: 'Updated {{title}}',
      createdTask: 'Created "{{title}}"',
      updatedTask: 'Updated "{{title}}"',
      csvPrefix: 'CSV · ',
      responseWith: 'Response with {{keys}}',
      count: {
        tasks_one: '{{count}} task',
        tasks_other: '{{count}} tasks',
        documents_one: '{{count}} document',
        documents_other: '{{count}} documents',
        members_one: '{{count}} member',
        members_other: '{{count}} members',
        hits_one: '{{count}} hit',
        hits_other: '{{count}} hits',
        results_one: '{{count}} result',
        results_other: '{{count}} results',
        rows_one: '{{count}} row',
        rows_other: '{{count}} rows',
        items_one: '{{count}} item',
        items_other: '{{count}} items',
      },
    },
  },
  artifacts: {
    task: 'Task',
    newTask: 'New task',
    newDocument: 'New document',
    document: 'Document',
    failed: 'failed',
    taskCreated: 'created',
    taskUpdated: 'updated',
    documentCreated: 'created',
    documentUpdated: 'updated',
    columns: {
      todo: 'To do',
      inProgress: 'In progress',
      done: 'Done',
      blocked: 'Blocked',
    },
    writing: 'Writing',
    words: '{{count}} words',
  },
  mode: {
    label: 'Mode',
    normal: 'Normal',
    high: 'High',
    max: 'Max',
    maxWarning: 'Credits are used faster in Max mode.',
  },
  lightbox: {
    open: 'View {{name}} at full size',
    download: 'Download image',
    openInNewTab: 'Open in new tab',
    close: 'Close',
  },
  citation: {
    source: 'Source {{index}}',
    openDocument: 'Open document',
  },
  workspace: {
    browser: {
      title: 'Browser',
      starting: 'Starting…',
      closed: 'The browser is closed',
      closedHint: 'Ask HUB to browse somewhere to start it again.',
      info: {
        title: 'About the shared browser',
        shared: 'Everyone with access to this chat can watch and drive the browser while it runs.',
        cookies:
          'Logins (cookies) belong to whoever started the session — the author of the latest message, or whoever took over.',
        persist:
          'They are saved encrypted per user and carried into the next session, across projects.',
        clear: 'Clear your saved logins under Settings → Browser.',
      },
      error: 'Error',
      agentControls: 'HUB is driving',
      userControls: 'You are driving',
      waitingForYou: 'HUB is waiting for you',
      takeControl: 'Take over',
      takeControlHint: 'Pauses HUB so you can drive the browser yourself',
      handBack: 'Hand back',
      handBackHint: 'Let HUB continue',
      close: 'Close the browser',
      hint: 'You can click and type directly in the view. Press Hand back (or Done in the view) when finished and HUB continues.',
      expand: 'Expand',
      collapse: 'Collapse',
    },
    title: 'Workspace',
    close: 'Close workspace',
    tasks: 'Tasks',
    documents: 'Documents',
    sources: 'Sources',
    attachments: 'Attachments',
    nothingYet: 'Nothing yet',
    document: 'document',
  },
  dev: {
    menu: 'Developer menu',
    nextReply: 'Next reply',
    normal: 'Normal reply',
    modes: {
      provider_capability: { label: 'Capability error', hint: 'model rejects — no retry' },
      provider_transient: { label: 'Transient error', hint: '429/timeout — retry' },
      hard_error: { label: 'Hard error', hint: 'the agent crashes' },
      unknown: { label: 'Unknown error', hint: 'unclassified' },
      tool_error: { label: 'Tool error', hint: 'a tool call fails' },
    },
    note: 'Applies to the next message you send, then the mode resets. Only visible locally for super admins.',
  },
  chats: {
    title: 'All chats',
    count_one: '{{count}} chat',
    count_other: '{{count}} chats',
    searchPlaceholder: 'Search chats...',
    all: 'All',
    noMatch: 'No chats match your search',
    empty: 'No chats yet',
    pinned: 'Pinned',
    recent: 'Recent',
    untitled: 'New chat',
    pin: 'Pin',
    unpin: 'Unpin',
    rename: 'Rename',
    delete: 'Delete',
    renameTitle: 'Rename chat',
    renameDescription: 'Enter a new name for the chat.',
    namePlaceholder: 'Chat name',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    deleteTitle: 'Delete chat',
    deleteDescription: 'Are you sure you want to delete "{{title}}"? This cannot be undone.',
    deleting: 'Deleting...',
    showArchived: 'Show archived',
    archived: 'Archived',
    unarchive: 'Restore',
  },
  credits: {
    exhaustedOrg: 'The organization is out of credits. Ask an admin to raise the limit.',
    exhaustedProject:
      'This project reached its credit cap. The project owner can raise or clear it.',
    exhaustedTts: 'Out of credits — read-aloud is paused.',
    exhaustedDictation: 'Out of credits — dictation is paused.',
  },
  collab: {
    unknownUser: 'Unknown',
  },
  onboarding: {
    title: 'Get started with the project',
    body: 'Tell HUB about the project so it is the right colleague from the start — a few short questions about the project, your role and what you want help with.',
    start: 'Start',
    starting: 'Starting…',
    failed: 'Could not start the conversation. Try again.',
    bannerTitle: 'Project kickoff',
    bannerBody:
      'This is where you set up the project. HUB asks a few short questions about the project, your role and what you want help with, and saves the answers in the project settings. You can skip any question — or all of them.',
    archivedNote: 'This chat is archived and hidden from the chat list.',
    unarchive: 'Restore',
  },
}

export default chat
